import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { get, post } from "../api";
import { PublicShell } from "../layout";
import { useSession, ROLE_LABELS, type Role } from "../session";
import { Button, ErrorBox, Input, Loading, Notice, fieldError } from "../ui";

function safeNext(next: string | null): string {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/espaces";
}

export function LoginPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post("/api/v1/auth/login", { email, password });
      await refresh();
      nav(safeNext(params.get("suite")));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <PublicShell>
      <h1>Se connecter</h1>
      <form className="stack" onSubmit={submit}>
        <ErrorBox error={error} />
        <Input label="Adresse e-mail" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input label="Mot de passe" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" busy={busy}>
          Se connecter
        </Button>
      </form>
      <p className="small">
        <Link to="/mot-de-passe-oublie">Mot de passe oublié</Link> · <Link to={`/inscription${params.get("suite") ? `?suite=${encodeURIComponent(params.get("suite")!)}` : ""}`}>Créer un compte</Link>
      </p>
    </PublicShell>
  );
}

export function SignupPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useSession();
  const [form, setForm] = useState({ full_name: "", email: params.get("email") ?? "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post("/api/v1/auth/signup", form);
      await refresh();
      nav(safeNext(params.get("suite")));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <PublicShell>
      <h1>Créer un compte</h1>
      <p className="muted">Un seul compte pour louer vos logements, candidater ou suivre votre location.</p>
      <form className="stack" onSubmit={submit}>
        <ErrorBox error={error} />
        <Input label="Nom et prénom" autoComplete="name" required value={form.full_name} error={fieldError(error, "full_name")} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
        <Input label="Adresse e-mail" type="email" autoComplete="email" required value={form.email} error={fieldError(error, "email")} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Input
          label="Mot de passe"
          type="password"
          autoComplete="new-password"
          required
          help="Au moins 10 caractères. Une phrase facile à retenir convient."
          value={form.password}
          error={fieldError(error, "password")}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
        <Button type="submit" busy={busy}>
          Créer mon compte
        </Button>
        <p className="small muted">Vos données sont utilisées pour la gestion de vos locations et ne sont jamais revendues.</p>
      </form>
      <p className="small">
        Déjà inscrit ? <Link to={`/connexion${params.get("suite") ? `?suite=${encodeURIComponent(params.get("suite")!)}` : ""}`}>Se connecter</Link>
      </p>
    </PublicShell>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <PublicShell>
      <h1>Mot de passe oublié</h1>
      {done ? (
        <Notice tone="succes">{done}</Notice>
      ) : (
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              const r = await post<{ message: string }>("/api/v1/auth/password/forgot", { email });
              setDone(r.message);
            } catch (err) {
              setError(err);
            } finally {
              setBusy(false);
            }
          }}
        >
          <ErrorBox error={error} />
          <Input label="Adresse e-mail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button type="submit" busy={busy}>
            Recevoir un lien
          </Button>
        </form>
      )}
      <p className="small">
        <Link to="/connexion">Retour à la connexion</Link>
      </p>
    </PublicShell>
  );
}

export function ResetPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <PublicShell>
      <h1>Choisir un nouveau mot de passe</h1>
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await post("/api/v1/auth/password/reset", { token: params.get("jeton") ?? "", password });
            nav("/connexion");
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}
      >
        <ErrorBox error={error} />
        <Input label="Nouveau mot de passe" type="password" autoComplete="new-password" required help="Au moins 10 caractères." value={password} error={fieldError(error, "password")} onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" busy={busy}>
          Enregistrer
        </Button>
      </form>
    </PublicShell>
  );
}

export function InvitationPage() {
  const { token = "" } = useParams();
  const { user, refresh } = useSession();
  const nav = useNavigate();
  const info = useQuery({ queryKey: ["invitation", token], queryFn: () => get<{ email: string; role: Role; org_name: string }>(`/api/v1/invitations/${token}`), retry: false });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const here = `/invitation/${token}`;
  return (
    <PublicShell>
      <h1>Invitation</h1>
      {info.isLoading ? <Loading /> : null}
      <ErrorBox error={info.error ?? error} />
      {info.data ? (
        <div className="stack">
          <p>
            Vous êtes invité à rejoindre l’espace <strong>{info.data.org_name}</strong> en tant que <strong>{ROLE_LABELS[info.data.role]}</strong>. Invitation adressée à {info.data.email}.
          </p>
          {user ? (
            <Button
              busy={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const r = await post<{ org_id: string }>(`/api/v1/invitations/${token}/accept`);
                  await refresh();
                  nav(`/espace/${r.org_id}`);
                } catch (err) {
                  setError(err);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Rejoindre l’espace
            </Button>
          ) : (
            <p>
              <Link className="bv-btn bv-btn-primaire" to={`/inscription?email=${encodeURIComponent(info.data.email)}&suite=${encodeURIComponent(here)}`}>
                Créer mon compte
              </Link>{" "}
              <Link className="bv-btn bv-btn-discret" to={`/connexion?suite=${encodeURIComponent(here)}`}>
                J’ai déjà un compte
              </Link>
            </p>
          )}
        </div>
      ) : null}
    </PublicShell>
  );
}
